/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

// ─── Menu Management Module — Entity Types ─────────────────────────────────────

// Brand is a global domain entity — re-export for convenience
export type { Brand, EntityCreatorAttribution } from '../../domain/entities';
import type { EntityCreatorAttribution } from '../../domain/entities';

// ─── Enums ──────────────────────────────────────────────────────────────────────

/** Kept for backward-compat but no longer used as the sole system — see `tag` field */
export enum MenuProfileType {
  REGULAR  = 'REGULAR',
  CAMPAIGN = 'CAMPAIGN',
  SPECIAL  = 'SPECIAL',
}

export enum MenuProfileStatus {
  DRAFT     = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED  = 'ARCHIVED',
}

// ─── Availability Schedule ──────────────────────────────────────────────────────

export type DayOfWeek = 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';
export const ALL_DAYS: DayOfWeek[] = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
export const DAY_LABELS: Record<DayOfWeek, string> = { MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun' };

export interface TimeSlot {
  /** HH:MM (24h), e.g. "08:00" */
  start: string;
  /** HH:MM (24h), e.g. "11:00" */
  end: string;
}

export interface AvailabilitySchedule {
  /** true = always available (ignore days/slots). false or undefined = use schedule. */
  alwaysOn?: boolean;
  /** Which days this entity is available */
  days: DayOfWeek[];
  /** Time slots per day (shared across all selected days). Multiple slots = multiple windows. */
  slots: TimeSlot[];
}

export const DEFAULT_SCHEDULE: AvailabilitySchedule = { alwaysOn: true, days: [...ALL_DAYS], slots: [{ start: '00:00', end: '23:59' }] };

// ─── Change Request ─────────────────────────────────────────────────────────────

export type ChangeRequestStatus = 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED';

export interface MenuChangeRequest {
  id: string;
  menuId: string;
  menuName: string;
  orgId: string;
  requestedBy: string;
  requestedByName: string;
  /** Full draft snapshot at time of submission */
  changes: string;
  /** Snapshot of live_data at time of submission for diff */
  previousData?: string;
  status: ChangeRequestStatus;
  adminNote?: string;
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedAt?: number;
  createdAt: number;
  /** Entity-level tracking for atomic approval */
  entityId?: string;
  entityType?: 'item' | 'category' | 'optionGroup' | 'profile';
  changeType?: 'MODIFY' | 'DELETE' | 'ADD' | 'CREATE_PROFILE';
  entityData?: string;
  /** ID/name of the department currently responsible for reviewing (pipeline stage) */
  currentStage?: string | null;
  /** Brand info from joined menu_profiles */
  brandName?: string | null;
  brandId?: string | null;
}

/** Lightweight pending entity descriptor returned by profile enrichment */
export interface PendingEntity {
  entityId: string;
  entityType: string;
  changeType: string;
  requestId: string;
  entityData?: string | null;
  previousData?: string | null;
}

// ─── Option Groups & Options ────────────────────────────────────────────────────

export interface MenuItemOption {
  id: string;
  nameAR: string;
  nameEN: string;
  extraPrice: number;
  /** If true, option is hidden from Digital Preview and customer view */
  hidden?: boolean;
  /** Reference to the source option in the Modifier Library (for master-link sync) */
  libraryOptionId?: string;
  /** If true, this option is the "pinned" representative price for the group */
  pinned?: boolean;
}

/** A named group of options / modifiers (e.g. "Choose your Bread", "Add-ons") */
export interface OptionGroup {
  id: string;
  nameEN: string;
  nameAR: string;
  options: MenuItemOption[];
  /** Minimum selections required (0 = optional). E.g. 1 = "Required" */
  minSelection?: number;
  /** Maximum selections allowed (0 = unlimited). E.g. 1 = single-select, 3 = "up to 3" */
  maxSelection?: number;
  /** If linked from Modifier Library, this is the library modifier ID. */
  linkedLibraryId?: string;
  /** If true, option group is hidden from Digital Preview and customer view */
  hidden?: boolean;
  /** When ON, Digital Preview renders quantity steppers instead of radio/checkbox */
  allowMultipleQty?: boolean;
  /** Relational modifier code for aggregator exports (e.g. "MCD-MOD-001") */
  groupCode?: string;
}

// ─── Menu Item ──────────────────────────────────────────────────────────────────

export interface MenuItem {
  id: string;
  nameAR: string;
  nameEN: string;
  descriptionAR?: string;
  descriptionEN?: string;
  basePrice: number;
  /** Internal food cost for financial analysis */
  cost?: number;
  /** R2 media key or full URL for item image */
  image?: string;
  /** Legacy flat options (migrated → optionGroups) */
  options: MenuItemOption[];
  /**
   * Relational pointers to library modifiers (source-of-truth).
   * Items reference modifiers by ID — never embed full objects.
   * The `optionGroups` array below is a HYDRATED view derived from
   * these IDs + the Modifier Library.  It is NOT persisted.
   */
  optionGroupIds?: string[];
  /** Hydrated option groups (derived from optionGroupIds + Library — NOT persisted) */
  optionGroups?: OptionGroup[];
  /** Sort order within category */
  sortOrder: number;
  /** If true, item is hidden from Digital Preview and customer view */
  hidden?: boolean;
  /** Internal code for POS / aggregator integration (e.g. KFC-ITM-001) */
  code?: string;
  /** If true, the modifier drawer will show a "Special Request" textarea for customer notes */
  allowSpecialRequests?: boolean;
  /** Time-based availability schedule */
  availabilitySchedule?: AvailabilitySchedule;
  /**
   * IDs of categories this item belongs to.
   * Enables multi-category linking — the same item record can appear in
   * multiple categories (e.g. "Burgers" + "Offers").
   * The first ID is treated as the "primary" category.
   */
  categoryIds?: string[];
  /** If set, this item was injected by a connected module and is read-only */
  systemManaged?: {
    /** Module ID that owns this item (e.g. 'aggregator') */
    sourceModule: string;
    /** Original entity ID in the source module (e.g. campaign item ID) */
    sourceEntityId: string;
  };
  /**
   * Campaign promotion metadata — attached when an item is injected from
   * the Aggregator module. Drives strikethrough pricing, expiry logic, and
   * revoke cleanup.
   */
  campaignMeta?: {
    campaignId: string;
    campaignName: string;
    discountValue: number;
    /** 'PERCENTAGE' or 'FLAT' */
    discountType: string;
    /** 'NONE' | 'CO_FUNDED' | 'FULLY_FUNDED' */
    fundingType: string;
    /** Sterling share for co-funded campaigns (0–100) */
    sterlingSharePct?: number;
    /** Partner share for co-funded campaigns (0–100) */
    partnerSharePct?: number;
    /** Original menu base price before any discount */
    originalPrice: number;
    /** Calculated promo price after discount */
    promoPrice: number;
    /** Campaign validity start (Unix ms) */
    startDate: number;
    /** Campaign validity end (Unix ms) */
    endDate: number;
  };
}

// ─── Category ───────────────────────────────────────────────────────────────────

export interface MenuCategory {
  id: string;
  nameAR: string;
  nameEN: string;
  items: MenuItem[];
  /** Sort order within profile */
  sortOrder: number;
  /** If true, category is hidden from Digital Preview and customer view */
  hidden?: boolean;
  /** Time-based availability schedule */
  availabilitySchedule?: AvailabilitySchedule;
  /** Internal code for POS / aggregator integration (e.g. KFC-CAT-001) */
  code?: string;
}

// ─── Ministry File / Permit (Simplified — attachment-only) ──────────────────────

export type VaultFileCategory = 'permit' | 'general';

export interface MinistryFile {
  id: string;
  /** Human-readable name (e.g. "Food Permit 2026") */
  name: string;
  /** Base64-encoded file data (image or PDF) */
  fileData: string;
  /** MIME type (e.g. "image/jpeg", "application/pdf") */
  fileType: string;
  /** IDs of menu profiles this permit covers */
  linkedProfileIds: string[];
  /** Upload metadata */
  uploadedBy: string;
  uploadedAt: number;
  orgId?: string;
  /** Category: 'permit' (ministry permits) or 'general' (general files). Defaults to 'permit'. */
  fileCategory?: VaultFileCategory;
  /** Source module that injected this file (e.g. 'aggregator'). Undefined = uploaded manually. */
  sourceModule?: string;
}

// ─── Menu Profile ───────────────────────────────────────────────────────────────

export interface MenuProfile extends EntityCreatorAttribution {
  id: string;
  name: string;
  brandId: string;
  brandName: string;
  branchIds: string[];
  /** Custom tag for type (e.g. "Regular", "Ramadan Special", "Catering") */
  tag: string;
  /** Legacy enum type — kept for backward compat; `tag` is the primary field */
  type: MenuProfileType;
  status: MenuProfileStatus;
  categories: MenuCategory[];
  /** Whether the ministry file needs re-certification after menu changes */
  permitSyncRequired: boolean;
  /** Linked ministry file IDs */
  ministryFileIds: string[];
  /** Legacy creator id alias retained for backward compatibility. */
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  orgId?: string;
  /**
   * IDs of platform modules allowed to push data into this menu.
   * Pulled from PLATFORM_MODULES registry — future modules auto-appear.
   * e.g. ['aggregator'] means the Aggregator can inject campaign items.
   */
  connectedModules?: string[];
  /** Whether the public preview link is active (default true) */
  publicLinkEnabled?: boolean;
  /** Epoch ms — when the public link expires (undefined = never) */
  publicLinkExpiry?: number;
  /** Profile-level availability schedule */
  availabilitySchedule?: AvailabilitySchedule;
  /** Whether a change request is pending approval */
  pendingChangeRequest?: string;
  /** Pending request type for card-level lock UX. */
  pendingChangeType?: MenuChangeRequest['changeType'];
  /** Entity-level pending items for granular locking */
  pendingEntities?: PendingEntity[];
  /** Admin toggle: when ON, non-admin users can see cost inspector in Digital Preview */
  costInspectorEnabled?: boolean;
}

// ─── Menu Settings ──────────────────────────────────────────────────────────────
/** A reusable modifier group stored in the Modifier Library. */
export interface LibraryModifier {
  id: string;
  nameEN: string;
  nameAR: string;
  options: MenuItemOption[];
  minSelection?: number;
  maxSelection?: number;
  /** Timestamp of last update — used for syncing linked copies. */
  updatedAt: number;
  orgId?: string;
  /** Brand this modifier belongs to (for scoping). */
  brandId?: string;
  /** Global hidden option IDs — toggled from the Library for store-wide out-of-stock */
  globalHiddenOptionIds?: string[];
}
export interface MenuSettings {
  /** Currency symbol shown in previews */
  currency: string;
  /** Default language for new profiles */
  defaultLanguage: 'AR' | 'EN';
  /** Secondary language code (e.g. 'AR', 'ES', 'FR') */
  secondaryLanguage: string;
  /** Ministry permit expiry warning threshold (days) */
  expiryWarningDays: number;
  /** When true, Manager publish actions create a change request instead of going live */
  requireApproval?: boolean;
  /** Emergency global shutdown — bypasses all schedules, marks entire menu as CLOSED */
  emergencyShutdown?: boolean;
  /** Global pricing strategy for zero-price items with mandatory groups */
  pricingStrategy?: PricingStrategy;
  /** When true, non-admin users can generate and share public menu links */
  allowPublicSharing?: boolean;
  /** When true, non-admin users can see the Export Menu action in preview */
  allowNonAdminMenuExport?: boolean;
  /** When true, non-admin users can open recipe/prep data in read-only mode */
  allowNonAdminRecipeAccess?: boolean;
  /** When true, non-admin users can see financial cost data in read-only mode */
  allowNonAdminCostAccess?: boolean;
  orgId?: string;
}

/** Strategy used to compute display price from mandatory option groups when base price is 0 */
export type PricingStrategy = 'LOWEST' | 'HIGHEST' | 'AVERAGE';

// ─── Ingredient Registry (Global Pantry) ────────────────────────────────────

export type PurchaseUnit = 'kg' | 'g' | 'L' | 'mL' | 'pc' | 'dozen' | 'box' | 'bag';

export const PURCHASE_UNITS: { value: PurchaseUnit; label: string }[] = [
  { value: 'kg', label: 'Kilogram (kg)' },
  { value: 'g', label: 'Gram (g)' },
  { value: 'L', label: 'Liter (L)' },
  { value: 'mL', label: 'Milliliter (mL)' },
  { value: 'pc', label: 'Piece (pc)' },
  { value: 'dozen', label: 'Dozen' },
  { value: 'box', label: 'Box' },
  { value: 'bag', label: 'Bag' },
];

export interface Ingredient {
  id: string;
  name: string;
  purchaseUnit: PurchaseUnit;
  purchasePrice: number;
  orgId: string;
  category?: string;
  updatedAt?: string;
}

export const ALLERGEN_LIST = [
  'Nuts', 'Dairy', 'Gluten', 'Eggs', 'Soy', 'Fish', 'Shellfish', 'Sesame', 'Mustard', 'Celery', 'Lupin', 'Sulphites',
] as const;

export type Allergen = typeof ALLERGEN_LIST[number];

// ─── Recipe (per Menu Item) ─────────────────────────────────────────────────

export interface RecipeIngredient {
  ingredientId: string;
  ingredientName: string;
  usageQty: number;
  usageUnit: PurchaseUnit;
  unitCost: number;
  lineCost: number;
}

export interface Recipe {
  id: string;
  menuItemId: string;
  profileId: string;
  orgId: string;
  ingredients: RecipeIngredient[];
  totalCost: number;
  prepSteps: string;
  standardYield: number;
  allergens: Allergen[];
  prepTime?: number;
}

export const SECONDARY_LANGUAGES = [
  { code: 'AR', name: 'Arabic', nativeName: 'العربية', dir: 'rtl' as const },
  { code: 'ES', name: 'Spanish', nativeName: 'Español', dir: 'ltr' as const },
  { code: 'FR', name: 'French', nativeName: 'Français', dir: 'ltr' as const },
  { code: 'TR', name: 'Turkish', nativeName: 'Türkçe', dir: 'ltr' as const },
  { code: 'UR', name: 'Urdu', nativeName: 'اردو', dir: 'rtl' as const },
  { code: 'HI', name: 'Hindi', nativeName: 'हिन्दी', dir: 'ltr' as const },
  { code: 'ZH', name: 'Chinese', nativeName: '中文', dir: 'ltr' as const },
  { code: 'PT', name: 'Portuguese', nativeName: 'Português', dir: 'ltr' as const },
  { code: 'DE', name: 'German', nativeName: 'Deutsch', dir: 'ltr' as const },
] as const;

export const CURRENCY_OPTIONS = [
  'QAR', 'AED', 'SAR', 'USD', 'EUR', 'GBP', 'INR', 'PKR', 'TRY', 'BRL', 'CNY',
] as const;

export const DEFAULT_MENU_SETTINGS: MenuSettings = {
  currency: 'QAR',
  defaultLanguage: 'EN',
  secondaryLanguage: 'AR',
  expiryWarningDays: 30,
  allowPublicSharing: false,
  allowNonAdminMenuExport: false,
  allowNonAdminRecipeAccess: false,
  allowNonAdminCostAccess: false,
};

// ─── Menu Data (Export-oriented shape used by Excel templates & image service) ─

export interface MenuData {
  title?: { en: string; ar?: string };
  brandName?: string;
  logo?: string;
  categories: {
    name: { en: string; ar?: string };
    items: {
      name: { en: string; ar?: string };
      description: { en: string; ar?: string };
      price: number;
      image?: string;
      choiceGroups: {
        name: { en: string; ar?: string };
        min: number;
        max: number;
        options: {
          name: { en: string; ar?: string };
          priceModifier?: number;
        }[];
      }[];
    }[];
  }[];
}
